import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserActivityService {
  private readonly SESSION_TIMEOUT = 60 * 60 * 1000; // 60 mins for testing
  private readonly WARNING_TIME = 5 * 60 * 1000;        // warn 5mins before timeout

  private readonly SESSION_STATE_KEY = 'session-state'; // 'active' | 'warning' | 'expired'
  private readonly LAST_ACTIVITY_KEY = 'last-activity';

  private lastActivity: number = Date.now();
  private warningTimer: any;
  private expirationTimer: any;

  private sessionWarningSubject = new BehaviorSubject<boolean>(false);
  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);

  public sessionWarning$ = this.sessionWarningSubject.asObservable();
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  constructor() {
    this.initFromStorage();
    this.initActivityTracking();
    this.startTimers();
    this.listenCrossTab();
  }

  /** Initialize last activity and session state from storage */
  private initFromStorage() {
    const storedActivity = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    const storedState = localStorage.getItem(this.SESSION_STATE_KEY);

    if (storedActivity) {
      const parsed = parseInt(storedActivity, 10);
       if (!isNaN(parsed)) {
        this.lastActivity = parsed;
        
        // If the last activity was too long ago (e.g., returning next day),
        // silently clear the expired state and reset - don't show popup
        const timeSinceActivity = Date.now() - parsed;
        if (timeSinceActivity > (30 * 60 * 1000)) {
          localStorage.removeItem(this.SESSION_STATE_KEY);
          this.lastActivity = Date.now();
          localStorage.setItem(this.LAST_ACTIVITY_KEY, this.lastActivity.toString());
          return; // Skip showing expired/warning popup
        }
      }
    }

    if (storedState === 'expired') {
      this.triggerSessionExpired();
    } else if (storedState === 'warning') {
      this.triggerSessionWarning();
    }
  }

  /** Track user events and reset timers */
  private initActivityTracking() {
    const events = ['mousemove','mousedown','keypress','scroll','touchstart','click','keydown'];
    const observables = events.map(e => fromEvent(document, e));

    merge(...observables)
      .pipe(debounceTime(200))
      .subscribe(() => this.handleUserActivity());
  }

  private handleUserActivity() {
    const state = localStorage.getItem(this.SESSION_STATE_KEY);

    // If we are in warning popup, ignore activity until user clicks Extend
    if (state === 'warning') {
      return;
    }

    this.lastActivity = Date.now();
    localStorage.setItem(this.LAST_ACTIVITY_KEY, this.lastActivity.toString());

    if (state === 'expired' || state === 'active') {
      localStorage.setItem(this.SESSION_STATE_KEY, 'active');
      this.sessionWarningSubject.next(false);
      this.sessionExpiredSubject.next(false);
    }

    this.resetTimers();
  }

  /** Setup warning and expiration timers */
  private startTimers() {
    this.resetTimers();
  }

  private resetTimers() {
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.expirationTimer) clearTimeout(this.expirationTimer);

    const timeSinceLast = Date.now() - this.lastActivity;
    const timeToWarning = this.SESSION_TIMEOUT - this.WARNING_TIME - timeSinceLast;
    const timeToExpire = this.SESSION_TIMEOUT - timeSinceLast;

    if (timeToWarning > 0) {
      this.warningTimer = setTimeout(() => this.broadcastSessionWarning(), timeToWarning);
    } else if (timeToWarning <= 0 && timeToExpire > 0) {
      // Already in warning period
      this.broadcastSessionWarning();
    }

    if (timeToExpire > 0) {
      this.expirationTimer = setTimeout(() => this.broadcastSessionExpired(), timeToExpire);
    } else {
      // Already expired
      this.broadcastSessionExpired();
    }
  }

  /** Broadcast warning to all tabs */
  private broadcastSessionWarning() {
    localStorage.setItem(this.SESSION_STATE_KEY, 'warning');
    this.triggerSessionWarning();
  }

  private broadcastSessionExpired() {
    localStorage.setItem(this.SESSION_STATE_KEY, 'expired');
    this.triggerSessionExpired();
  }

  /** Trigger warning in this tab */
  private triggerSessionWarning() {
    this.sessionWarningSubject.next(true);
  }

  /** Trigger expiration in this tab */
  private triggerSessionExpired() {
    this.sessionExpiredSubject.next(true);
    this.clearTimers();
  }

  /** Listen for storage changes from other tabs */
  private listenCrossTab() {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key) return;

      if (event.key === this.SESSION_STATE_KEY) {
        if (event.newValue === 'warning') this.triggerSessionWarning();
        else if (event.newValue === 'expired') this.triggerSessionExpired();
        else if (event.newValue === 'active') {
          this.sessionWarningSubject.next(false);
          this.sessionExpiredSubject.next(false);
          this.resetTimers();
        }
      }

      if (event.key === this.LAST_ACTIVITY_KEY && event.newValue) {
        const newActivity = parseInt(event.newValue, 10);
        if (!isNaN(newActivity) && newActivity > this.lastActivity) {
          this.lastActivity = newActivity;
          this.resetTimers();
        }
      }
    });
  }

  private clearTimers() {
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.expirationTimer) clearTimeout(this.expirationTimer);
  }

  /** Manually extend session (user clicks “Extend”) */
  public extendSession(): void {
    this.lastActivity = Date.now();
    localStorage.setItem(this.LAST_ACTIVITY_KEY, this.lastActivity.toString());
    localStorage.setItem(this.SESSION_STATE_KEY, 'active');
    this.sessionWarningSubject.next(false);
    this.resetTimers();
  }

  public isUserActive(): boolean {
    return (Date.now() - this.lastActivity) < this.SESSION_TIMEOUT;
  }

  public getRemainingTime(): number {
    return Math.max(0, this.SESSION_TIMEOUT - (Date.now() - this.lastActivity));
  }

    // Backward compatibility methods
  public setPopupActive(active: boolean): void {
    // This method exists for compatibility with existing components
    // The new implementation handles popup state internally
  }
 
  public resetActivity(): void {
    // Reset activity - same as extend session
    this.extendSession();
  }
 
  public clearAllTimers(): void {
    // Clear all timers for cleanup
    this.clearTimers();
  }
 
  public getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivity;
  }
}
